import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Phone, ChefHat, CalendarCheck, Truck, PoundSterling, MessageSquare, PackageCheck, Star, MapPin, ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";
import { PHONE, PHONE_TEL } from "@/components/PublicLayout";
import { api } from "@/lib/api";

const HERO_IMG = "https://customer-assets-eiarnc6j.emergentagent.net/wingman/1b579b6a-2d4e-44eb-97e9-b3d7a8a3bfc9/attachments/6e1ed4dac57043099e423384bc7b5731_3ABA52FB-2E5E-486D-B35D-13991AA325DA.png";
const MOBILE_HERO_IMG = "https://customer-assets-eiarnc6j.emergentagent.net/wingman/1b579b6a-2d4e-44eb-97e9-b3d7a8a3bfc9/attachments/005eebaf02624649939f485b2cd6d22f_3420BA1A-04C5-425C-998E-E0F4F10D7D87.png";
const POD_IMG = "https://customer-assets-eiarnc6j.emergentagent.net/wingman/1b579b6a-2d4e-44eb-97e9-b3d7a8a3bfc9/attachments/91fad3efa0ae4ad3995b038d0b4dc48f_IMG_3103.jpeg";

const BENEFITS = [
  { Icon: ChefHat, title: "Fully Equipped", text: "Every pod comes with a hob, oven, fridge, dishwasher and washing machine — everything your family needs to cook, clean and live as normal." },
  { Icon: CalendarCheck, title: "Flexible Hire Periods", text: "From a few weeks to several months, our mobile kitchen hire in Surrey flexes around your project timeline — extend anytime." },
  { Icon: Truck, title: "Prompt, Professional Delivery", text: "We deliver, position, plumb and connect your temporary kitchen at a time that suits you, and collect it when you're done." },
  { Icon: PoundSterling, title: "Competitive Pricing", text: "Honest, competitive rates with no hidden extras. Far cheaper than moving the family into rented accommodation." },
];

const STEPS = [
  { Icon: MessageSquare, title: "Get in Touch", text: "Tell us your location, your renovation start date, and how long you'll need the unit." },
  { Icon: Truck, title: "We Deliver & Connect", text: "We deliver the trailer directly to your driveway and connect the electrics and water supply." },
  { Icon: ChefHat, title: "Cook as Normal", text: "Use your fully-equipped kitchen as normal throughout your renovation." },
  { Icon: PackageCheck, title: "We Collect", text: "When your kitchen is finished, we come back and collect the trailer." },
];

const FAQS = [
  { q: "How long does it take to set up a mobile kitchen?", a: "We typically deliver and have your temporary kitchen fully connected and ready to use within a few hours. Our team handles all the plumbing and electrical connections before we leave." },
  { q: "What size driveway do I need?", a: "Our trailers are compact enough to fit on most standard driveways. Get in touch and tell us your driveway dimensions — we'll confirm whether we can position the unit before you commit." },
  { q: "How long can I hire a temporary kitchen for?", a: "We offer flexible hire periods from a few weeks to several months. If your renovation overruns, you can simply extend your hire at any time." },
  { q: "Do you cover insurance claims?", a: "Yes — we work with homeowners whose kitchens are out of action due to water damage or insurance-related repairs, not just planned renovations. Your loss adjuster or insurer can contact us directly." },
  { q: "How far do you deliver?", a: "We deliver across Surrey and into South West London — generally within a 20-mile radius of our Weybridge base. Call us on 07740051799 to confirm your area." },
];

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map(({ q, a }) => ({
    "@type": "Question",
    name: q,
    acceptedAnswer: { "@type": "Answer", text: a },
  })),
};

const AREAS = ["Weybridge", "Walton-on-Thames", "Esher", "Cobham", "Guildford", "Woking", "Leatherhead", "Epsom", "Kingston upon Thames", "Richmond", "Twickenham", "Staines-upon-Thames", "Farnham", "Dorking", "Wimbledon"];

export function StarRating({ rating = 5 }) {
  return (
    <span className="flex gap-0.5" aria-label={`${rating} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} className={`h-4 w-4 ${i <= rating ? "fill-[#1A237E] text-[#1A237E]" : "text-slate-300"}`} />
      ))}
    </span>
  );
}

export default function Home() {
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    api.get("/reviews").then((res) => setReviews(res.data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <SEO
        title="Temporary Kitchen Hire Surrey | Mobile Kitchen Company | Weybridge & Guildford"
        description="Surrey's temporary kitchen trailer hire specialists. We deliver a fully-equipped mobile kitchen to your driveway while your kitchen is being renovated. Serving Weybridge, Guildford & South West London. Call 07740051799."
        extraSchema={FAQ_SCHEMA}
      />

      <section className="relative min-h-[calc(100vh-153px)] min-h-[calc(100svh-153px)] flex flex-col bg-[#1A237E] lg:bg-transparent lg:h-[calc(100vh-141px)] lg:min-h-0 lg:grid lg:grid-cols-2 lg:grid-rows-[minmax(0,1fr)]" data-testid="hero-section">
        <img
          src={MOBILE_HERO_IMG}
          alt="Mobile kitchen trailer delivered to a customer's driveway by Mobile Kitchen Company"
          className="block w-full h-auto lg:hidden"
        />
        <img
          src={HERO_IMG}
          alt="Mobile kitchen trailer delivered to a customer's driveway by Mobile Kitchen Company"
          className="hidden lg:block lg:w-full lg:order-2 lg:h-full lg:object-cover lg:object-[50%_62%]"
        />
        <div className="flex-1 bg-[#1A237E] lg:flex-none lg:flex lg:items-center lg:order-1 lg:h-full">
          <div className="w-full px-6 pt-3 pb-6 lg:px-10 lg:py-8" data-testid="hero-panel">
            <p className="text-white/80 font-semibold tracking-wide uppercase text-sm mb-2">
              Mobile Kitchen Hire · Temporary Kitchen Hire · Surrey
            </p>
            <h1 className="text-xl sm:text-4xl lg:text-4xl xl:text-5xl font-bold text-white leading-tight">
              Keep Cooking During Your Kitchen Renovation
            </h1>
            <p className="mt-2 lg:mt-6 text-base lg:text-lg text-white leading-relaxed max-w-3xl">
              We deliver a fully-equipped mobile kitchen trailer to your driveway so you can carry on cooking
              while your kitchen is being renovated. Serving Weybridge, Guildford, Surrey, and South West London.
            </p>
            <div className="mt-3 lg:mt-6 flex flex-wrap gap-2">
              <Link
                to="/contact"
                data-testid="hero-quote-button"
                className="px-7 py-2.5 border border-white text-white font-semibold rounded hover:bg-white hover:text-[#1A237E] transition-colors"
              >
                Get a Free Quote
              </Link>
              <a
                href={PHONE_TEL}
                data-testid="hero-call-button"
                className="flex items-center gap-2 px-7 py-2.5 border border-white text-white font-semibold rounded hover:bg-white hover:text-[#1A237E] transition-colors"
              >
                <Phone className="h-5 w-5" /> Call {PHONE}
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20 bg-white" data-testid="about-intro-section">
        <div className="max-w-7xl mx-auto px-4 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1A237E]">
              Trusted Local Specialists in Mobile Kitchen Hire
            </h2>
            <p className="mt-5 text-base text-slate-700 leading-relaxed">
              Mobile Kitchen Company (UK) Limited is a Surrey-based, family-run business providing mobile
              kitchen hire and temporary kitchen hire across Surrey and the surrounding areas. We understand
              how disruptive a kitchen renovation, water damage repair or building project can be — which is
              why our temporary kitchen pods let you carry on cooking, washing and living at home while the
              work is done.
            </p>
            <p className="mt-4 text-base text-slate-700 leading-relaxed">
              Our fully equipped kitchen pods are delivered to your drive or garden, professionally connected,
              and collected once your project is complete. No takeaways every night, no moving out — just
              normal family life.
            </p>
            <p className="mt-4 text-base text-slate-700 leading-relaxed">
              We also work with homeowners dealing with water damage and insurance repairs — keeping families
              at home and cooking while remedial works are completed.
            </p>
            <Link to="/about" data-testid="about-intro-link" className="mt-6 inline-flex items-center gap-2 text-[#1A237E] font-semibold hover:text-[#1A237E] transition-colors">
              Find out more about us <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <img
            src={POD_IMG}
            alt="Temporary kitchen trailer set up on site with door open"
            className="rounded shadow-lg w-full object-cover aspect-[4/3]"
            data-testid="about-intro-image"
          />
        </div>
      </section>

      <section className="py-16 sm:py-20 bg-[#1A237E]" data-testid="why-choose-us-section">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold text-white">Why Choose Us for Temporary Kitchen Hire in Surrey?</h2>
            <p className="mt-4 text-lg text-white/80">
              We make mobile kitchen hire simple, affordable and stress-free from first call to final collection.
            </p>
          </div>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {BENEFITS.map(({ Icon, title, text }, i) => (
              <div key={title} className="bg-white border border-slate-200 rounded p-6 hover:shadow-md transition-shadow" data-testid={`benefit-card-${i + 1}`}>
                <div className="h-11 w-11 rounded bg-[#1A237E] flex items-center justify-center">
                  <Icon className="h-5 w-5 text-white" />
                </div>
                <h3 className="mt-4 text-lg font-semibold text-[#1A237E]">{title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20 bg-white" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1A237E]">How Temporary Kitchen Trailer Hire Works</h2>
            <p className="mt-4 text-lg text-slate-600">Four simple steps and your temporary kitchen is up and running.</p>
          </div>
          <div className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map(({ Icon, title, text }, i) => (
              <div key={title} className="relative text-center" data-testid={`step-card-${i + 1}`}>
                <div className="mx-auto h-14 w-14 rounded-full bg-[#1A237E] text-white flex items-center justify-center text-xl font-bold" style={{ fontFamily: "'Merriweather', Georgia, serif" }}>
                  {i + 1}
                </div>
                <h3 className="mt-4 text-xl font-semibold text-[#1A237E]">{title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed max-w-xs mx-auto">{text}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/how-it-works" data-testid="how-it-works-link" className="inline-flex items-center gap-2 text-[#1A237E] font-semibold hover:text-[#1A237E] transition-colors">
              See the full process <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20 bg-[#1A237E]" data-testid="testimonials-preview-section">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold text-white">What Our Customers Say</h2>
            <p className="mt-4 text-lg text-white/80">Real reviews from families who stayed at home with our temporary kitchen hire in Surrey.</p>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {reviews.map((r) => (
              <figure key={r.id} className="bg-white border border-slate-200 rounded p-6 flex flex-col" data-testid={`review-preview-${r.reviewer_name.toLowerCase().replace(/\s+/g, "-")}`}>
                <StarRating rating={r.rating} />
                <blockquote className="mt-4 text-sm text-slate-700 leading-relaxed flex-1 line-clamp-6">{r.text}</blockquote>
                <figcaption className="mt-4 pt-4 border-t border-slate-100 text-sm">
                  <span className="font-semibold text-[#1A237E]">{r.reviewer_name}</span>
                  <span className="text-slate-500"> · {r.platform}{r.date ? ` · ${r.date}` : ""}</span>
                </figcaption>
              </figure>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/testimonials" data-testid="read-more-reviews-link" className="inline-flex items-center gap-2 px-6 py-3 border border-white text-white font-semibold rounded hover:bg-white hover:text-[#1A237E] transition-colors">
              Read More Reviews <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20 bg-white" data-testid="service-areas-section">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1A237E]">Areas We Cover</h2>
            <p className="mt-4 text-lg text-slate-600">
              Homeowners across Surrey trust The Mobile Kitchen Company when their kitchen is out of action during a renovation.
            </p>
          </div>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            {AREAS.map((area) => (
              <span key={area} className="flex items-center gap-1.5 px-4 py-2 bg-[#F8FAFC] border border-slate-200 rounded text-sm font-medium text-[#1A237E]" data-testid={`area-chip-${area.toLowerCase().replace(/[\s-]+/g, "-")}`}>
                <MapPin className="h-3.5 w-3.5 text-[#1A237E]" /> {area}
              </span>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20 bg-[#1A237E]" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-2xl sm:text-3xl font-bold text-white text-center">Frequently Asked Questions</h2>
          <div className="mt-10 space-y-4">
            {FAQS.map(({ q, a }, i) => (
              <div key={q} className="bg-white border border-slate-200 rounded p-6" data-testid={`faq-item-${i + 1}`}>
                <h3 className="text-lg font-semibold text-[#1A237E]">{q}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#1A237E] py-16" data-testid="cta-banner">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white">Need a Temporary Kitchen? Get Your Free Quote Today</h2>
          <p className="mt-4 text-lg text-slate-300 max-w-2xl mx-auto">
            Speak to our friendly team about mobile kitchen hire in Surrey — we'll have you cooking again in no time.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link to="/contact" data-testid="cta-quote-button" className="px-7 py-3.5 bg-white text-[#1A237E] font-semibold rounded hover:bg-slate-100 transition-colors">
              Get a Free Quote
            </Link>
            <a href={PHONE_TEL} data-testid="cta-call-button" className="flex items-center gap-2 px-7 py-3.5 bg-white text-[#1A237E] font-semibold rounded hover:bg-slate-100 transition-colors">
              <Phone className="h-5 w-5" /> Call {PHONE}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
